/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectCreate: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifiedNGOs, setVerifiedNGOs] = useState<any[]>([]);
  const [selectedNGOs, setSelectedNGOs] = useState<string[]>([]);
  const [implementationType, setImplementationType] = useState<'ngo_led' | 'direct'>('ngo_led');

  const [formData, setFormData] = useState({
    name: '',
    summary: '',
    thematic_area: '',
    budget: '',
    start_date: '',
    end_date: '',
    reporting_cadence: 'monthly',
    beneficiary_profile: '',
    locations: [{ name: '', state: '', district: '' }],
    metrics: [{ name: '', target: '', unit: '' }],
    tranche_plan: [{ tranche_number: 1, amount: '', planned_date: '' }],
    proposal_content: {
      problem_statement: '',
      strategy_approach: '',
      planned_activities: '',
      expected_outcomes: ''
    }
  });

  useEffect(() => {
    if (companyId) {
      loadVerifiedNGOs();
    }
  }, [companyId]);

  const loadVerifiedNGOs = async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from('ngos')
      .select('*')
      .eq('company_id', companyId)
      .eq('status', 'verified')
      .order('legal_name', { ascending: true });

    if (!error && data) {
      setVerifiedNGOs(data);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleToggleNGO = (ngoId: string) => {
    setSelectedNGOs(prev =>
      prev.includes(ngoId)
        ? prev.filter(id => id !== ngoId)
        : [...prev, ngoId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (implementationType === 'ngo_led' && selectedNGOs.length === 0) {
      setError('Please select at least one NGO to assign this project to');
      return;
    }

    if (implementationType === 'direct' && (!formData.thematic_area || !formData.budget)) {
      setError('Please fill in all project settings for direct implementation');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !companyId) throw new Error('Not authenticated');

      // Create project
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          company_id: companyId,
          name: formData.name,
          summary: formData.summary,
          implementation_type: implementationType,
          thematic_area: implementationType === 'direct' ? formData.thematic_area : null,
          budget: implementationType === 'direct' ? parseFloat(formData.budget) : null,
          start_date: implementationType === 'direct' ? formData.start_date || null : null,
          end_date: implementationType === 'direct' ? formData.end_date || null : null,
          reporting_cadence: implementationType === 'direct' ? formData.reporting_cadence : null,
          beneficiary_profile: implementationType === 'direct' ? formData.beneficiary_profile : null,
          locations: implementationType === 'direct' ? formData.locations : null,
          metrics: implementationType === 'direct' ? formData.metrics.map(m => ({
            metric: m.name,
            target: parseFloat(m.target) || 0,
            unit: m.unit
          })) : [],
          tranche_plan: implementationType === 'direct' ? formData.tranche_plan.map(t => ({
            ...t,
            amount: parseFloat(t.amount) || 0
          })) : null,
          proposal_content: implementationType === 'direct' ? formData.proposal_content : null,
          status: implementationType === 'direct' ? 'active' : 'assigned',
          created_by: user.id
        })
        .select()
        .single();

      if (projectError) throw projectError;

      // Create metrics for direct implementation
      if (implementationType === 'direct') {
        for (const metric of formData.metrics) {
          if (metric.name && metric.target) {
            await supabase
              .from('project_metrics_progress')
              .insert({
                project_id: project.id,
                metric_name: metric.name,
                target_value: parseFloat(metric.target),
                unit: metric.unit
              });
          }
        }
      }

      // Assign project to selected NGOs ONLY if NGO-led
      if (implementationType === 'ngo_led') {
        for (const ngoId of selectedNGOs) {
          const { error: assignError } = await supabase
            .from('project_ngos')
            .insert({
              project_id: project.id,
              ngo_id: ngoId,
              status: 'pending', // NGO needs to fill details and accept
              assigned_by: user.id
            });

          if (assignError) throw assignError;
        }
      }

      navigate(`/client/company/projects`);
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { name: '', state: '', district: '' }]
    });
  };

  const removeLocation = (index: number) => {
    setFormData({
      ...formData,
      locations: formData.locations.filter((_, i) => i !== index)
    });
  };

  const addMetric = () => {
    setFormData({
      ...formData,
      metrics: [...formData.metrics, { name: '', target: '', unit: '' }]
    });
  };

  const removeMetric = (index: number) => {
    setFormData({
      ...formData,
      metrics: formData.metrics.filter((_, i) => i !== index)
    });
  };

  const addTranche = () => {
    const nextNum = formData.tranche_plan.length + 1;
    setFormData({
      ...formData,
      tranche_plan: [...formData.tranche_plan, { tranche_number: nextNum, amount: '', planned_date: '' }]
    });
  };

  const removeTranche = (index: number) => {
    setFormData({
      ...formData,
      tranche_plan: formData.tranche_plan.filter((_, i) => i !== index)
        .map((t, idx) => ({ ...t, tranche_number: idx + 1 }))
    });
  };

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Create New Project</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Enter project information and choose the implementation mode.
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <h2>Project Information</h2>

          <div className="form-group">
            <label>Project Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter project name"
            />
          </div>

          <div className="form-group">
            <label>Objective / Summary *</label>
            <textarea
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              required
              placeholder="Brief project objective and summary"
              rows={5}
            />
          </div>

          <div className="form-group" style={{ marginTop: '2rem' }}>
            <label>Implementation Mode *</label>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <label className={`card ${implementationType === 'ngo_led' ? 'selected-mode' : ''}`} style={{
                flex: 1,
                cursor: 'pointer',
                border: implementationType === 'ngo_led' ? '2px solid var(--primary)' : '2px solid var(--border)',
                background: implementationType === 'ngo_led' ? 'var(--primary-light)' : 'white',
                padding: '1rem',
                textAlign: 'center'
              }}>
                <input
                  type="radio"
                  name="implementationType"
                  value="ngo_led"
                  checked={implementationType === 'ngo_led'}
                  onChange={() => setImplementationType('ngo_led')}
                  style={{ display: 'none' }}
                />
                <strong>NGO-Led</strong>
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>Assign to verified NGOs</p>
              </label>

              <label className={`card ${implementationType === 'direct' ? 'selected-mode' : ''}`} style={{
                flex: 1,
                cursor: 'pointer',
                border: implementationType === 'direct' ? '2px solid var(--primary)' : '2px solid var(--border)',
                background: implementationType === 'direct' ? 'var(--primary-light)' : 'white',
                padding: '1rem',
                textAlign: 'center'
              }}>
                <input
                  type="radio"
                  name="implementationType"
                  value="direct"
                  checked={implementationType === 'direct'}
                  onChange={() => setImplementationType('direct')}
                  style={{ display: 'none' }}
                />
                <strong>Direct Implementation</strong>
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>Implemented by your company</p>
              </label>
            </div>
          </div>

          {implementationType === 'ngo_led' ? (
            <>
              <h2 style={{ marginTop: '2rem' }}>Assign to NGO</h2>
              {verifiedNGOs.length === 0 ? (
                <div className="card" style={{ background: '#fef3c7', border: '1px solid #f59e0b', padding: '1.5rem' }}>
                  <p style={{ margin: 0, color: '#92400e' }}>
                    <strong>No Verified NGOs Available</strong>
                    <br />
                    You need to verify at least one NGO before creating NGO-led projects.
                  </p>
                </div>
              ) : (
                <div className="card">
                  <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
                    Select one or more verified NGOs to assign this project to.
                  </p>
                  <div className="ngo-list">
                    {verifiedNGOs.map((ngo) => (
                      <div
                        key={ngo.id}
                        className={`ngo-item ${selectedNGOs.includes(ngo.id) ? 'selected' : ''}`}
                        onClick={() => handleToggleNGO(ngo.id)}
                        style={{
                          padding: '1rem',
                          border: `2px solid ${selectedNGOs.includes(ngo.id) ? 'var(--primary)' : 'var(--border)'}`,
                          borderRadius: '8px',
                          marginBottom: '0.75rem',
                          cursor: 'pointer',
                          background: selectedNGOs.includes(ngo.id) ? 'var(--primary-light)' : 'white',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <input
                            type="checkbox"
                            checked={selectedNGOs.includes(ngo.id)}
                            readOnly
                          />
                          <div style={{ flex: 1 }}>
                            <strong>{ngo.legal_name}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="card" style={{ marginTop: '2rem' }}>
              <h2>Project Settings (Direct)</h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div className="form-group">
                  <label>Thematic Area *</label>
                  <input
                    type="text"
                    name="thematic_area"
                    value={formData.thematic_area}
                    onChange={handleChange}
                    required={implementationType === 'direct'}
                    placeholder="e.g. Education, Healthcare"
                  />
                </div>
                <div className="form-group">
                  <label>Total Budget (₹) *</label>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    required={implementationType === 'direct'}
                    placeholder="Enter total project budget"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required={implementationType === 'direct'}
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    required={implementationType === 'direct'}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reporting Cadence</label>
                <select
                  name="reporting_cadence"
                  value={formData.reporting_cadence}
                  onChange={(e) => setFormData({ ...formData, reporting_cadence: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Beneficiary Profile</label>
                <textarea
                  name="beneficiary_profile"
                  value={formData.beneficiary_profile}
                  onChange={handleChange}
                  placeholder="Describe the target beneficiaries"
                  rows={3}
                />
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Project Locations</h3>
              {formData.locations.map((loc, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', marginBottom: '0.5rem', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Village/Area</label>
                    <input value={loc.name} onChange={e => {
                      const newLocs = [...formData.locations];
                      newLocs[idx].name = e.target.value;
                      setFormData({ ...formData, locations: newLocs });
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>District</label>
                    <input value={loc.district} onChange={e => {
                      const newLocs = [...formData.locations];
                      newLocs[idx].district = e.target.value;
                      setFormData({ ...formData, locations: newLocs });
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>State</label>
                    <input value={loc.state} onChange={e => {
                      const newLocs = [...formData.locations];
                      newLocs[idx].state = e.target.value;
                      setFormData({ ...formData, locations: newLocs });
                    }} />
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeLocation(idx)}
                    style={{
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      lineHeight: 1,
                      minWidth: '24px'
                    }}
                    title="Remove Location"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addLocation} style={{ marginTop: '0.5rem' }}>+ Add Location</button>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Metrics & Targets</h3>
              {formData.metrics.map((m, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px auto', gap: '1rem', marginBottom: '0.5rem', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Metric Name</label>
                    <input value={m.name} onChange={e => {
                      const newMetrics = [...formData.metrics];
                      newMetrics[idx].name = e.target.value;
                      setFormData({ ...formData, metrics: newMetrics });
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Target</label>
                    <input type="number" value={m.target} onChange={e => {
                      const newMetrics = [...formData.metrics];
                      newMetrics[idx].target = e.target.value;
                      setFormData({ ...formData, metrics: newMetrics });
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Unit</label>
                    <input value={m.unit} onChange={e => {
                      const newMetrics = [...formData.metrics];
                      newMetrics[idx].unit = e.target.value;
                      setFormData({ ...formData, metrics: newMetrics });
                    }} />
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeMetric(idx)}
                    style={{
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      lineHeight: 1,
                      minWidth: '24px'
                    }}
                    title="Remove Metric"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addMetric} style={{ marginTop: '0.5rem' }}>+ Add Metric</button>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Funding Schedule (Tranches)</h3>
              {formData.tranche_plan.map((t, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr auto', gap: '1rem', marginBottom: '0.5rem', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Tranche #</label>
                    <input readOnly value={t.tranche_number} style={{ background: '#f5f5f5' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Amount (₹)</label>
                    <input type="number" value={t.amount} onChange={e => {
                      const newTranches = [...formData.tranche_plan];
                      newTranches[idx].amount = e.target.value;
                      setFormData({ ...formData, tranche_plan: newTranches });
                    }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.75rem' }}>Date</label>
                    <input type="date" value={t.planned_date} onChange={e => {
                      const newTranches = [...formData.tranche_plan];
                      newTranches[idx].planned_date = e.target.value;
                      setFormData({ ...formData, tranche_plan: newTranches });
                    }} />
                  </div>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => removeTranche(idx)} style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addTranche} style={{ marginTop: '0.5rem' }}>+ Add Tranche</button>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Proposal Details</h3>
              <div className="form-group">
                <label>Problem Statement</label>
                <textarea rows={3} value={formData.proposal_content.problem_statement} onChange={e => setFormData({
                  ...formData,
                  proposal_content: { ...formData.proposal_content, problem_statement: e.target.value }
                })} />
              </div>
              <div className="form-group">
                <label>Strategy / Approach</label>
                <textarea rows={3} value={formData.proposal_content.strategy_approach} onChange={e => setFormData({
                  ...formData,
                  proposal_content: { ...formData.proposal_content, strategy_approach: e.target.value }
                })} />
              </div>
              <div className="form-group">
                <label>Planned Activities</label>
                <textarea rows={3} value={formData.proposal_content.planned_activities} onChange={e => setFormData({
                  ...formData,
                  proposal_content: { ...formData.proposal_content, planned_activities: e.target.value }
                })} />
              </div>
              <div className="form-group">
                <label>Expected Outcomes</label>
                <textarea rows={3} value={formData.proposal_content.expected_outcomes} onChange={e => setFormData({
                  ...formData,
                  proposal_content: { ...formData.proposal_content, expected_outcomes: e.target.value }
                })} />
              </div>
            </div>
          )}

          <div className="form-actions" style={{ marginTop: '2rem' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/client/company/projects`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || (implementationType === 'ngo_led' && (selectedNGOs.length === 0 || verifiedNGOs.length === 0))}
            >
              {loading ? 'Creating...' : (implementationType === 'direct' ? 'Create Project' : 'Create & Assign Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCreate;

